import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import BackgroundShapes from './BackgroundShapes';
import SwiperCardSlider from './SwiperCardSlider';
import PreFooter from './PreFooter';

interface HomeLayoutProps {
  children: React.ReactNode;
}

const HomeLayout: React.FC<HomeLayoutProps> = ({ children }) => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // App screenshots for the slider
  
  // Get current path for active link styling
  const currentPath = location.pathname === '/' ? 'home' : location.pathname.substring(1);
  
  return (
    <div className="min-h-screen bg-white text-gray-900 flex flex-col overflow-hidden relative">
      {/* Background shapes */}
      <BackgroundShapes />
      
      {/* Tree SVG Background */}
      <div className="absolute inset-0 flex justify-center pointer-events-none z-0 overflow-hidden">
        <img 
          src="/assets/tree.svg" 
          alt="" 
          className="h-full w-auto opacity-[0.03] object-contain" 
          aria-hidden="true"
        />
      </div>
      
      {/* Header */}
      <header className="relative flex justify-between items-center px-8 py-4 z-10">
        <div className="flex items-center">
          <Link to="/">
            <img 
              src="/assets/logo.png" 
              alt="SOKOVIBE LOGO" 
              className="h-16 w-auto cursor-pointer" 
            />
          </Link>
        </div>
        
        {/* Centered navigation */}
        <nav className="hidden md:flex space-x-8 absolute left-1/2 transform -translate-x-1/2">
          <Link 
            to="/" 
            className={`${currentPath === 'home' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
          >
            Home
          </Link>
          <Link 
            to="/about" 
            className={`${currentPath === 'about' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
          >
            About
          </Link>
          <Link 
            to="/services" 
            className={`${currentPath === 'services' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
          >
            Services
          </Link>
        </nav>
        
        {/* Mobile menu button */}
        <button 
          className="md:hidden text-purple-800"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2"
              viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round"
                  d="M4 6h16M4 12h16m-7 6h7"></path>
          </svg>
        </button>
      </header>
      
      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="md:hidden absolute top-20 left-0 right-0 bg-white shadow-lg z-20 py-4 px-8"
          >
            <div className="flex flex-col space-y-4">
              <Link 
                to="/" 
                className={`text-left ${currentPath === 'home' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/about" 
                className={`text-left ${currentPath === 'about' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
                onClick={() => setMobileMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/services" 
                className={`text-left ${currentPath === 'services' ? 'text-teal-500' : 'text-purple-800'} hover:text-teal-500 transition-colors`}
                onClick={() => setMobileMenuOpen(false)}
              >
                Services
              </Link>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Main content without slider - full width */}
      {children}
      
      {/* Pre-Footer with notification form */}
      <PreFooter />
      
      {/* Footer */}
      <footer className="w-full bg-white border-t border-gray-200 py-4 mt-auto z-10 shadow-sm">
        <div className="container mx-auto px-6 flex flex-col md:flex-row justify-between items-center">
          <div className="text-sm text-purple-700 font-medium">
            &copy; {new Date().getFullYear()} <span className="text-purple-700 font-bold">SokoVibe</span>. All rights reserved.
          </div>
          <div className="flex flex-col md:flex-row space-y-4 md:space-y-0 md:space-x-6">
            <div className="flex space-x-6">
              <Link 
                to="/faq" 
                className="text-sm text-purple-700 font-medium hover:text-purple-700 transition-colors"
              >
                FAQ
              </Link>
              <Link 
                to="/privacy" 
                className="text-sm text-purple-700 font-medium hover:text-purple-700 transition-colors"
              >
                Privacy Policy
              </Link>
            </div>
          
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeLayout;
